// TROIA-V3-FRONTEND/src/modules/crm/seller-dashboard/components/SellerHeroCta.tsx
import React from 'react';
import type { LeadCaptureSession } from '@troia-v3/types';

import { Button } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import '../seller-dashboard.css';

interface SellerHeroCtaProps {
  pending: LeadCaptureSession[];
  onStart: () => void;
  onResume: (session: LeadCaptureSession) => void;
}

/**
 * Texto da pílula por STATUS da sessão (G3 da onda final). Antes, TODA sessão
 * pendente dizia "aguardando WhatsApp" — inclusive uma `waiting_scan`, que
 * nem cliente tem ainda (ninguém leu o QR), e uma `confirmed`/`form_completed`,
 * que já passou dessa etapa. O vendedor lia "aguardando WhatsApp · " com o
 * nome vazio e não entendia o que fazer.
 *
 * O prefixo "1 atendimento" é o do mockup 07 variante B.
 */
function describeSession(session: LeadCaptureSession): string {
  const name = session.customer?.name?.trim();
  const suffix = name ? ` · ${name}` : '';
  if (session.status === 'waiting_scan') return 'QR aberto, aguardando o cliente';
  if (session.status === 'filled') return `1 atendimento aguardando WhatsApp${suffix}`;
  // `confirmed` e `form_completed`: cliente já confirmou, falta o vendedor
  // encerrar (ou preencher o formulário).
  return `1 atendimento em andamento${suffix}`;
}

/**
 * Qual sessão o "Retomar" abre quando há mais de uma. Sessões COM lead
 * (`filled`/`confirmed`/`form_completed`) vêm antes das `waiting_scan`: um QR
 * aberto e nunca lido vive 7 dias (TTL da sessão) e ficaria eternamente no
 * topo, escondendo o atendimento real que precisa de ação. `sort` é estável
 * em JS, então dentro de cada grupo a ordem original é preservada.
 */
function mostActionable(pending: LeadCaptureSession[]): LeadCaptureSession | undefined {
  return [...pending].sort((a, b) => Number(Boolean(b.leadId)) - Number(Boolean(a.leadId)))[0];
}

/** Hero "Pronto para atender?" — mockup 07 (.hero-cta), variante B com pendente. */
export const SellerHeroCta: React.FC<SellerHeroCtaProps> = ({ pending, onStart, onResume }) => {
  const first = mostActionable(pending);
  // Com várias sessões abertas o nome de UMA delas confundiria — mostra a
  // contagem, e o "Retomar" leva à mais acionável.
  const label = pending.length === 1 && first
    ? describeSession(first)
    : `${pending.length} atendimentos abertos`;
  return (
    <div className="seller-hero" data-testid="seller-hero">
      <div className="seller-hero__icon"><Icon name="qr-code" size={20} /></div>
      <div className="min-w-0">
        <div className="text-[17px] font-bold tracking-[-.02em]" style={{ color: 'var(--theme-text)' }}>Pronto para atender?</div>
        <div className="text-[13px] mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
          Gere o QR Code e o cliente se cadastra no próprio celular. O lead já entra com você.
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2.5 flex-none flex-wrap justify-end">
        {first && (
          <>
            <span className="seller-hero__pending" data-testid="seller-hero-pending" data-status={first.status}>
              <Icon name="clock" size={14} />{label}
            </span>
            <Button variant="secondary" size="sm" onClick={() => onResume(first)} data-testid="seller-hero-resume">Retomar</Button>
          </>
        )}
        <Button variant="primary" size="sm" icon="qr-code" onClick={onStart} data-testid="seller-hero-start">Iniciar atendimento</Button>
      </div>
    </div>
  );
};
SellerHeroCta.displayName = 'SellerHeroCta';
